import { type FC, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { CheckIcon, XCloseIcon } from '@/components/ui/icons'
import { Modal } from '@/components/ui/modal'
import { ModalFooter, ModalHeader } from '@/components/ui/modal-atoms'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { OpsVettingImage } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useGetGalleryImages } from '@/features/service-vetting/query/use-get-gallery-images.ts'
import { useTranslation } from '@/translations/i18n'

interface ServiceVettingCriteriaChoosePhotoModalProps {
	title: string
	onClose: () => void
	onConfirm: (imageId: number) => void
	isPending?: boolean
	currentSelectedImageId?: number
	serviceId: number
	galleryId: number
}
export const ServiceVettingCriteriaChoosePhotoModal: FC<
	ServiceVettingCriteriaChoosePhotoModalProps
> = ({
	title,
	onClose,
	onConfirm,
	isPending,
	currentSelectedImageId,
	serviceId,
	galleryId
}) => {
	const { t } = useTranslation()
	const imagesQuery = useGetGalleryImages({ serviceId, galleryId })
	const [selectedImgId, setSelectedImgId] = useState<number | null>(
		currentSelectedImageId || null
	)
	return (
		<Modal
			className='grid max-h-[680px] grid-rows-[repeat(2,auto)_minmax(0,1fr)_auto] overflow-y-hidden'
			width={'680'}
		>
			<ModalHeader>
				<ButtonLegacy
					onClick={onClose}
					shape='square'
					size={'sm'}
					type='button'
					variant={'tertiary'}
				>
					<XCloseIcon />
				</ButtonLegacy>
				{title}
			</ModalHeader>
			<Separator orientation='horizontal' />
			<ScrollArea scrollbar={<Scrollbar />}>
				<div className='grid grid-cols-[repeat(3,minmax(0,1fr))] gap-6 p-6'>
					{imagesQuery.isPending
						? Array.from(
								{ length: 9 },
								(_, i) => `skeleton-${i}`
							).map((key) => <Skeleton key={key} />)
						: imagesQuery.data?.map((image) => (
								<button
									className='relative cursor-pointer'
									key={image.id}
									onClick={() => setSelectedImgId(image.id)}
									type='button'
								>
									<OpsVettingImage src={image.url} />
									{selectedImgId === image.id && (
										<div className='absolute top-2 right-2 flex size-8 items-center justify-center rounded-2xl bg-neutral-white/90'>
											<CheckIcon />
										</div>
									)}
								</button>
							))}
				</div>
			</ScrollArea>
			<ModalFooter>
				<ButtonLegacy
					disabled={isPending}
					onClick={onClose}
					type='button'
					variant={'tertiary'}
				>
					{t('common:cancel')}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={
						(selectedImgId &&
							selectedImgId === currentSelectedImageId) ||
						isPending
					}
					onClick={() => selectedImgId && onConfirm(selectedImgId)}
					type='button'
				>
					{t('common:confirm')} {isPending && <Spinner size={20} />}
				</ButtonLegacy>
			</ModalFooter>
		</Modal>
	)
}
